<template>
  <div class="event-stream">
    <div class="stream-head">
      <div class="t">{{ task.title }}</div>
      <div class="s">
        <span class="badge" :class="'s-' + task.status">{{ task.statusText }}</span>
        <span v-if="task.pointsCharged" class="cost">已消耗 {{ task.pointsCharged }} 点</span>
        <span v-if="task.pointsRefunded" class="refund">已退还 {{ task.pointsRefunded }} 点</span>
      </div>
    </div>

    <!-- ★事件永远向下追加、不覆盖：用户能往上翻，看到它是怎么一步步做出来的 -->
    <div v-for="ev in events" :key="ev.id" class="ev">
      <!-- 读图 -->
      <template v-if="ev.type === 'profile'">
        <div class="bubble">
          <div class="line">我看了你的图 —— <b>{{ ev.payload.category }}</b></div>
          <div class="sub">{{ ev.payload.appearance }}</div>
          <div v-if="ev.payload.visibleText && ev.payload.visibleText.length" class="sub">
            图上读到：{{ ev.payload.visibleText.join('、') }}
          </div>
        </div>
      </template>

      <!-- 选风格 -->
      <template v-else-if="ev.type === 'style'">
        <div class="bubble">
          <div class="line">
            我打算用 <b>{{ styleName(ev.payload.setId) }}</b>，出 <b>{{ ev.payload.totalPages }}</b> 页
          </div>
          <div v-if="ev.payload.reason" class="sub">{{ ev.payload.reason }}</div>
          <div v-if="ev.payload.clamped" class="warn">页数超出该风格能力，已自动调整</div>
        </div>
      </template>

      <!-- 大纲 -->
      <template v-else-if="ev.type === 'outline'">
        <div class="bubble wide">
          <div class="line">文案拟好了：</div>
          <div v-for="p in ev.payload.pages" :key="p.index" class="page-row">
            <span class="idx">{{ String(p.index).padStart(2, '0') }}</span>
            <div class="pg">
              <div class="h">{{ p.headline }}</div>
              <div class="sh">{{ p.subheadline }}</div>
            </div>
          </div>
        </div>
      </template>

      <!-- 视觉方向 -->
      <template v-else-if="ev.type === 'visual'">
        <div class="bubble">
          <div class="line">配色和字体定了</div>
          <div class="palette">
            <span v-for="(c, k) in ev.payload.palette" :key="k" class="sw" :style="{ background: c }" :title="k + ' ' + c"></span>
          </div>
          <div class="sub">{{ ev.payload.mood }}</div>
        </div>
      </template>

      <!-- 生图进度（最慢的一步，每张 20~35 秒，必须让用户看到在动） -->
      <template v-else-if="ev.type === 'bg-start'">
        <div class="bubble slim">
          <i class="el-icon-loading"></i>
          正在画第 {{ ev.payload.i }}/{{ ev.payload.total }} 张底图<span v-if="ev.payload.pages">（给第 {{ ev.payload.pages.join('、') }} 页用）</span>…
        </div>
      </template>
      <template v-else-if="ev.type === 'bg-done'">
        <div class="bubble slim">
          <el-image v-if="ev.payload.thumbUrl" :src="ev.payload.thumbUrl" fit="cover" class="bg-thumb" />
          <span v-else>
            ✓ 第 {{ ev.payload.i }}/{{ ev.payload.total }} 张底图好了<span v-if="ev.payload.cached">（命中缓存，没花钱）</span>
          </span>
        </div>
      </template>

      <!-- 单页渲染完成 -->
      <!-- 每页排版完就带图 —— 渲完当场传了 COS，用户能一页页看着成品落位 -->
      <template v-else-if="ev.type === 'page-rendered'">
        <div class="bubble slim page-row">
          <el-image
            v-if="ev.payload.url"
            :src="ev.payload.url"
            fit="cover"
            class="page-thumb"
            :preview-src-list="[ev.payload.url]"
          />
          <span>✓ 第 {{ ev.payload.index }}/{{ ev.payload.total }} 页排版好了</span>
        </div>
      </template>
      <template v-else-if="ev.type === 'scene'">
        <div class="bubble slim">
          这套图用了 {{ ev.payload.bgCount }} 张底图<span v-if="ev.payload.generated === 0">（全部命中缓存，本次没花钱）</span>
        </div>
      </template>


      <!-- ★闸门：只有"当前仍在等待"的那一个才给按钮，历史闸门只留一行摘要。
           三个闸门的内容完全不同，所以按 gate 分派到不同卡片 -->
      <template v-else-if="ev.type === 'gate'">
        <div class="bubble gate">
          <template v-if="isActiveGate(ev)">
            <!-- 闸门①：产品认对了吗 + 风格 + 页数（还没花钱，随便改） -->
            <CardStyleGate
              v-if="ev.payload.gate === 'style'"
              :payload="ev.payload.suspendPayload || {}"
              :submitting="submitting"
              @submit="(p) => submitReview('style', p.action, p)"
              @cancel="submitReview('style', 'cancel')"
            />

            <!-- 闸门②：文案 + 换风格（★扣费点） -->
            <CardOutlineGate
              v-else-if="ev.payload.gate === 'outline'"
              :payload="ev.payload.suspendPayload || {}"
              :points-cost="pointsCost"
              :submitting="submitting"
              @submit="(p) => submitReview('outline', p.action, p)"
              @cancel="submitReview('outline', 'cancel')"
            />

            <!-- 闸门③：成品（改文案免费秒出） -->
            <CardReviewGate
              v-else
              :payload="ev.payload.suspendPayload || {}"
              :submitting="submitting"
              @submit="(p) => submitReview('review', p.action, p)"
            />
          </template>
          <div v-else class="sub done-gate">✓ 已确认（{{ gateName(ev.payload.gate) }}）</div>
        </div>
      </template>

      <!-- 改稿没改成 —— 成品还在，点数没动，只是这次要求没做到 -->
      <template v-else-if="ev.type === 'revise-failed'">
        <div class="bubble warn-soft">
          <i class="el-icon-warning-outline"></i>
          {{ ev.payload.message || '这次没改成，作品保持原样' }}
          <div class="sub">作品还在，点数也没扣</div>
        </div>
      </template>

      <!-- 出错 -->
      <template v-else-if="ev.type === 'error'">
        <div class="bubble err">
          <i class="el-icon-warning-outline"></i> 「{{ ev.payload.step }}」这一步没成功
        </div>
      </template>
    </div>

    <!-- 生成中 -->
    <div v-if="task.status === 'running'" class="bubble slim running">
      <i class="el-icon-loading"></i> 正在干活……
    </div>

    <!-- 失败 -->
    <div v-if="task.status === 'failed'" class="bubble err">
      {{ task.errorMsg || '生成失败了' }}
    </div>

    <!-- ★结果：按发布器长 —— 用户的终点是发布，不是拿到一堆 PNG -->
    <div v-if="task.status === 'done' && task.result" class="result">
      <div class="r-head">
        做好了，可以直接发
        <!-- ★定稿不等于锁死：带着上次的决策快照重跑，只重渲受影响的页，免费 -->
        <el-button v-if="!revising" size="mini" plain @click="revising = true">再改一版</el-button>
      </div>

      <div v-if="revising" class="revise-box">
        <el-input
          v-model="reviseText"
          type="textarea"
          :rows="2"
          placeholder="想改哪里？例如：标题换成「通勤党的续命水杯」、第2页文案再口语化一点"
        />
        <div class="revise-btns">
          <el-button type="primary" size="small" :loading="submitting" :disabled="!reviseText.trim()" @click="onRevise">
            按这个改（不扣点）
          </el-button>
          <el-button size="small" :disabled="submitting" @click="revising = false">取消</el-button>
        </div>
      </div>
      <div class="r-body">
        <div class="images">
          <el-image
            v-for="(u, i) in task.result.images || []"
            :key="i"
            :src="u"
            fit="cover"
            class="r-img"
            :preview-src-list="task.result.images"
          />
        </div>
        <div v-if="noteCopy" class="copy">
          <div class="c-row">
            <div class="c-label">标题</div>
            <div class="c-val">{{ noteCopy.title }}</div>
            <el-button size="mini" @click="copy(noteCopy.title)">复制</el-button>
          </div>
          <div class="c-row">
            <div class="c-label">正文</div>
            <div class="c-val pre">{{ noteCopy.body }}</div>
            <el-button size="mini" @click="copy(noteCopy.body)">复制</el-button>
          </div>
          <div class="c-row">
            <div class="c-label">标签</div>
            <div class="c-val">{{ tagText }}</div>
            <el-button size="mini" @click="copy(tagText)">复制</el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CardStyleGate from './card-style-gate.vue'
import CardOutlineGate from './card-outline-gate.vue'
import CardReviewGate from './card-review-gate.vue'
import { STYLE_NAME, GATE_NAME } from '../constants'

export default {
  name: 'EventStream',
  components: { CardStyleGate, CardOutlineGate, CardReviewGate },
  props: {
    task: { type: Object, required: true },
    events: { type: Array, default: () => [] },
    /** 闸门②按钮上要显示"扣几点"，从员工配置来 */
    pointsCost: { type: Number, default: 0 },
  },
  data() {
    return {
      submitting: false,
      revising: false,
      reviseText: '',
    }
  },
  computed: {
    noteCopy() {
      return (this.task.result && this.task.result.noteCopy) || null
    },
    tagText() {
      const tags = (this.noteCopy && this.noteCopy.tags) || []
      return tags.map((t) => '#' + t).join(' ')
    },
  },
  methods: {
    styleName(id) {
      return STYLE_NAME[id] || id
    },
    gateName(g) {
      return GATE_NAME[g] || g
    },

    /**
     * ★这个闸门还在等人吗？
     *   一次运行可能挂起多轮（改一次→再挂起一次），事件流里会有多张闸门卡。
     *   只有「任务确实处于待确认」且「这是最后一张闸门卡」才给按钮，
     *   否则用户会看到一堆历史按钮、点了还会报"页面数据已过期"。
     */
    isActiveGate(ev) {
      if (this.task.status !== 'awaiting_review') return false
      const gates = this.events.filter((e) => e.type === 'gate')
      return gates.length > 0 && gates[gates.length - 1].id === ev.id
    },

    /** 定稿之后回来改 —— 走 revise（新起一个 run，带快照），不是 resume */
    async onRevise() {
      this.submitting = true
      try {
        await new Promise((resolve, reject) =>
          this.$emit('revise', { instruction: this.reviseText.trim(), resolve, reject }),
        )
        this.revising = false
        this.reviseText = ''
      } catch (e) {
        // 接口层已经弹过提示
      } finally {
        this.submitting = false
      }
    },

    /**
     * 统一提交闸门决定。
     * ★payload 里的 action 由各闸门卡自己决定（approve / change / edit / cancel），
     *   这里只负责转交给父组件去调接口 —— 父组件握着 taskGuid 和轮询，
     *   确认完必须立刻重启轮询，否则用户点完按钮就再也看不到后续进度。
     */
    async submitReview(gate, action, payload) {
      this.submitting = true
      try {
        // action 已经单独传了，别再塞进 payload 里重复一遍
        const rest = { ...(payload || {}) }
        delete rest.action
        await new Promise((resolve, reject) =>
          this.$emit('review', { gate, action, payload: rest, resolve, reject }),
        )
      } catch (e) {
        // 接口层已经弹过提示了，这里只需要恢复按钮
      } finally {
        this.submitting = false
      }
    },
    copy(text) {
      const ta = document.createElement('textarea')
      ta.value = text || ''
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      this.$message.success('已复制')
    },
  },
}
</script>

<style lang="scss">
.event-stream {
  padding: 24px 32px 60px;
  /* 行长超过 860 就不好读了；宽屏下居中，别让内容贴左、右边空一大块 */
  max-width: 860px;
  margin: 0 auto;

  .stream-head {
    margin-bottom: 18px;
    .t { font-size: 18px; font-weight: 600; color: #1f2329; }
    .s { margin-top: 6px; font-size: 12px; display: flex; align-items: center; gap: 10px; }
    .badge { padding: 2px 8px; border-radius: 10px; background: #eef0f4; color: #606266; }
    .badge.s-running { background: #e8efff; color: #4b7bff; }
    .badge.s-awaiting_review { background: #fff3e0; color: #f0a020; }
    .badge.s-done { background: #e8f8ec; color: #2aa54a; }
    .badge.s-failed { background: #fdecec; color: #f56c6c; }
    .cost { color: #f08c3a; }
    .refund { color: #2aa54a; }
  }

  .ev { margin-bottom: 12px; }
  .bubble {
    display: inline-block;
    max-width: 100%;
    padding: 12px 16px;
    background: #f6f7f9;
    border-radius: 12px;
    font-size: 14px;
    color: #1f2329;
    line-height: 1.7;
    .line b { color: #4b7bff; }
    .sub { margin-top: 4px; font-size: 12px; color: #8a8f98; }
    .warn { margin-top: 4px; font-size: 12px; color: #f0a020; }
  }
  .bubble.wide { display: block; }
  .bubble.slim { padding: 8px 12px; font-size: 13px; color: #606266; }
  .bubble.gate { background: #fff8ec; display: block; min-width: 320px; }
  .gate-btns { margin-top: 10px; display: flex; gap: 8px; }
  .gate-edit { margin-top: 10px; }
  .done-gate { color: #a0a4ab; }
  .bubble.err { background: #fdecec; color: #f56c6c; }
  .bubble.running { color: #4b7bff; }

  .page-row { display: flex; margin-top: 8px; }
  .idx { width: 30px; color: #b0b4bb; font-size: 12px; padding-top: 2px; }
  .pg .h { font-size: 14px; color: #1f2329; }
  .pg .sh { font-size: 12px; color: #8a8f98; }

  .palette { display: flex; gap: 6px; margin: 8px 0 4px; }
  .sw { width: 22px; height: 22px; border-radius: 5px; border: 1px solid rgba(0, 0, 0, 0.08); }

  .bg-thumb { width: 78px; height: 104px; border-radius: 6px; }
  /* 出图是 9:16，缩略图也按这个比例，别把竖图塞进 3:4 的框里挤变形 */
  /* 改稿没改成：提醒但不吓人 —— 东西都还在，不是红色错误 */
  .bubble.warn-soft {
    background: #fff8ec; color: #a06a12; font-size: 13px; line-height: 1.6;
    i { margin-right: 5px; }
    .sub { margin-top: 4px; font-size: 12px; color: #b99a63; }
  }

  .page-row { display: flex; align-items: center; gap: 10px; }
  .page-thumb { width: 72px; height: 128px; border-radius: 6px; flex-shrink: 0; cursor: zoom-in; }

  .result {
    margin-top: 22px;
    border: 1px solid #ebedf0;
    border-radius: 12px;
    overflow: hidden;
    .r-head {
      padding: 12px 18px; background: #f6f7f9;
      font-size: 14px; font-weight: 600; color: #1f2329;
      display: flex; align-items: center; justify-content: space-between;
    }
    .revise-box { padding: 14px 18px 0; }
    .revise-btns { margin-top: 10px; display: flex; gap: 8px; }
    .r-body { padding: 18px; }
    .images { display: flex; flex-wrap: wrap; gap: 10px; }
    .r-img { width: 132px; height: 176px; border-radius: 8px; cursor: zoom-in; }
    .copy { margin-top: 18px; }
    .c-row { display: flex; align-items: flex-start; gap: 10px; padding: 8px 0; border-top: 1px dashed #ebedf0; }
    .c-label { width: 40px; font-size: 12px; color: #8a8f98; padding-top: 3px; flex-shrink: 0; }
    .c-val { flex: 1; font-size: 13px; color: #1f2329; line-height: 1.7; }
    .c-val.pre { white-space: pre-wrap; }
  }
}
</style>
